// "Highlights" dropdown — bullet key features pulled from the product
// description (list items first, spec-table rows as fallback). Lists that
// sit under a "What's in the box" style heading are skipped.
import { useMemo, useState } from 'react';
import { ChevronDown, Star } from 'lucide-react';

const BOX_RE = /in the box|box contents|package|packing list|what'?s included|includes:/i;
const MAX_ITEMS = 8;

function precedingLabel(el) {
        let prev = el.previousElementSibling;
        for (let i = 0; prev && i < 2; i += 1, prev = prev.previousElementSibling) {
                const t = (prev.textContent || '').trim();
                if (t) return t.slice(0, 80);
        }
        return '';
}

function clean(s) {
        return String(s || '').replace(/\s+/g, ' ').trim();
}

export function extractKeyFeatures(html) {
        if (!html || typeof DOMParser === 'undefined') return [];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const out = [];
        const seen = new Set();
        const push = (s) => {
                const t = clean(s);
                if (t.length < 3 || t.length > 180 || seen.has(t.toLowerCase())) return;
                seen.add(t.toLowerCase());
                out.push(t);
        };
        for (const list of doc.querySelectorAll('ul, ol')) {
                if (list.closest('li')) continue; // nested lists are folded into their parent item
                if (BOX_RE.test(precedingLabel(list))) continue;
                for (const li of list.querySelectorAll(':scope > li')) {
                        const own = li.cloneNode(true);
                        own.querySelectorAll('ul, ol').forEach((n) => n.remove());
                        const sub = li.querySelector('ul > li, ol > li');
                        const head = clean(own.textContent);
                        push(sub && head ? `${head} — ${clean(sub.textContent)}` : head || sub?.textContent);
                }
                if (out.length >= MAX_ITEMS) return out.slice(0, MAX_ITEMS);
        }
        if (out.length) return out;
        for (const table of doc.querySelectorAll('table')) {
                if (BOX_RE.test(precedingLabel(table))) continue;
                for (const tr of table.querySelectorAll('tr')) {
                        const cells = Array.from(tr.querySelectorAll('th, td')).map((c) => clean(c.textContent));
                        if (cells.length >= 2 && cells[0] && cells[1] && !BOX_RE.test(cells[0])) push(`${cells[0]}: ${cells[1]}`);
                }
                if (out.length >= MAX_ITEMS) break;
        }
        return out.slice(0, MAX_ITEMS);
}

export default function KeyFeatures({ html }) {
        const items = useMemo(() => extractKeyFeatures(html), [html]);
        const [open, setOpen] = useState(false);
        if (!items.length) return null;
        return (
                <section data-testid="pdp-key-features" className="mt-6 border-t border-b border-white/10">
                        <button
                                type="button"
                                onClick={() => setOpen((o) => !o)}
                                aria-expanded={open}
                                data-testid="pdp-key-features-toggle"
                                className="w-full flex items-center justify-between py-4 group"
                        >
                                <span className="flex items-center gap-3 font-display text-[16px] uppercase tracking-[0.02em]">
                                        <Star className="h-4 w-4 text-[#FF9F1C]" /> Highlights
                                </span>
                                <ChevronDown className={'h-4 w-4 text-white/60 transition-transform duration-200 group-hover:text-white ' + (open ? 'rotate-180' : '')} />
                        </button>
                        {open && (
                                <ul data-testid="pdp-key-features-list" className="pb-5 space-y-2">
                                        {items.map((t) => (
                                                <li key={t} className="flex gap-3 font-sans text-[13px] text-white/75 leading-relaxed">
                                                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-[#FF9F1C]" />
                                                        {t}
                                                </li>
                                        ))}
                                </ul>
                        )}
                </section>
        );
}
